import type { ReactNode } from "react";
import { Icon } from "../../design-review/MemberReferencePreview";
import { MemberBrand } from "../shared/MemberBrand";
import type { Transaction, TransactionGroup } from "../shared/types";
import { money, navigate, statusLabel } from "../shared/ui";
import { readTransactionGroup, transactionPath } from "./transaction-groups";
import { TransactionActions } from "./TransactionActions";

export function MemberTopBar({
  title,
  backTo,
  onBack,
  action,
  backIcon,
}: {
  readonly title?: string;
  readonly backTo?: string;
  readonly onBack?: () => void;
  readonly action?: ReactNode;
  readonly backIcon?: ReactNode;
}) {
  return (
    <header className="mr-topbar">
      <div className="mr-topbar__side">
        {backTo ? (
          <button className="mr-icon-button" type="button" aria-label="뒤로" onClick={() => onBack ? onBack() : navigate(backTo)}>
            {backIcon ?? <Icon name="back" />}
          </button>
        ) : (
          !title && <button className="member-mobile-brand" type="button" aria-label="CarePass 홈" onClick={() => navigate("/app")}>
            <MemberBrand />
          </button>
        )}
      </div>
      {title && <h1 className="mr-topbar__title">{title}</h1>}
      <div className="mr-topbar__side mr-topbar__side--end">{action}</div>
    </header>
  );
}

function compactDate(value: string) {
  const parsed = new Date(/(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat("ko-KR", { month: "numeric", day: "numeric", timeZone: "Asia/Seoul" }).format(parsed);
}

export function MemberTransactionRow({ item, group = readTransactionGroup(), showActions = true }: { readonly item: Transaction; readonly group?: TransactionGroup; readonly showActions?: boolean }) {
  return (
    <><button className="mr-transaction-row" type="button" onClick={() => navigate(transactionPath(`/app/transactions/${item.id}`, group))}>
      <span className="mr-transaction-row__icon"><Icon name="receipt" size={20} /></span>
      <span className="mr-transaction-row__copy">
        <strong>{item.title}</strong>
        <span className={`mr-status mr-status--${(item.status ?? "unknown").toLowerCase()}`}>{statusLabel(item.status)}</span>
      </span>
      <span className="mr-transaction-row__meta">
        <strong>{money(item.approvedAmount ?? item.requestedAmount)}</strong>
        <small>{compactDate(item.submittedAt)}</small>
      </span>
      <Icon name="chevron" size={17} />
    </button>
    {showActions && <TransactionActions transaction={item} group={group} layout="row" />}
    </>
  );
}

export function MemberEmpty({ icon = "receipt", title, description, action }: {
  readonly icon?: "receipt" | "gift" | "help";
  readonly title: string;
  readonly description: string;
  readonly action?: ReactNode;
}) {
  return (
    <div className="mr-empty">
      <span><Icon name={icon} /></span>
      <strong>{title}</strong>
      <p>{description}</p>
      {action}
    </div>
  );
}

export { Icon };

export function MemberBottomNavigation({ pathname }: { readonly pathname: string }) {
  const links = [["/app", "홈", "home"], ["/app/transactions", "거래", "receipt"], ["/app/benefits", "혜택", "gift"], ["/app/my", "MY", "user"]] as const;
  return <nav className="mobile-nav mr-bottom-nav" aria-label="회원 빠른 메뉴">{links.map(([href, label, icon]) => <button key={href} type="button" aria-current={pathname === href || (href !== "/app" && pathname.startsWith(`${href}/`)) ? "page" : undefined} onClick={() => navigate(href)}><Icon name={icon} size={24}/><span>{label}</span></button>)}</nav>;
}

export function MemberWizardScreen({ title, backTo, step, onPrevious, children }: { readonly title: string; readonly backTo: string; readonly step: number; readonly onPrevious: () => void; readonly children: ReactNode }) {
 return <div className="member-secondary-screen"><MemberTopBar title={title} backTo={backTo} onBack={() => step > 1 ? onPrevious() : navigate(backTo)} action={<button type="button" className="mr-icon-button" aria-label="닫기" onClick={() => navigate(backTo)}>×</button>}/><div className="member-secondary-screen__content"><div className="member-wizard">{children}</div></div></div>;
}
